/**
 * Pure, deterministic Minesweeper Flags engine.
 *
 * This module is the single source of truth for game rules and is used by the
 * authoritative server (the Durable Object). Every function is side-effect free
 * apart from mutating the passed-in state, and board generation accepts an RNG
 * so games are reproducible in tests.
 */
import { BOARD_SIZE, CELL_COUNT, MINE_COUNT, WIN_THRESHOLD, BOMB_RADIUS } from "./constants.js";
import type { ServerCell, ClientCell, GameStatus, Scores, Team } from "./types.js";
import { otherTeam } from "./types.js";

export interface ServerGameState {
  cells: ServerCell[];
  scores: Scores;
  turn: Team;
  status: GameStatus;
  winner: Team | null;
  bombsUsed: Record<Team, boolean>;
  /** Total mines claimed so far (== scores.red + scores.blue). */
  minesClaimed: number;
}

export type Rng = () => number;

export function index(x: number, y: number): number {
  return y * BOARD_SIZE + x;
}

export function coords(i: number): { x: number; y: number } {
  return { x: i % BOARD_SIZE, y: Math.floor(i / BOARD_SIZE) };
}

export function inBounds(x: number, y: number): boolean {
  return x >= 0 && x < BOARD_SIZE && y >= 0 && y < BOARD_SIZE;
}

/** Indices of the (up to 8) neighbours of cell i. */
export function neighbours(i: number): number[] {
  const { x, y } = coords(i);
  const out: number[] = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const nx = x + dx;
      const ny = y + dy;
      if (inBounds(nx, ny)) out.push(index(nx, ny));
    }
  }
  return out;
}

/** Create an empty (all-hidden, no-mine) board. */
function emptyCells(): ServerCell[] {
  return Array.from({ length: CELL_COUNT }, () => ({
    mine: false,
    adjacent: 0,
    revealed: false,
    owner: null,
  }));
}

/**
 * Generate a fresh board with MINE_COUNT mines placed via a partial
 * Fisher–Yates shuffle over cell indices, then precompute adjacency counts.
 */
export function generateBoard(rng: Rng = Math.random, mineCount = MINE_COUNT): ServerCell[] {
  const cells = emptyCells();
  const indices = Array.from({ length: CELL_COUNT }, (_, i) => i);
  for (let i = 0; i < mineCount; i++) {
    const j = i + Math.floor(rng() * (CELL_COUNT - i));
    const tmp = indices[i]!;
    indices[i] = indices[j]!;
    indices[j] = tmp;
    cells[indices[i]!]!.mine = true;
  }
  for (let i = 0; i < CELL_COUNT; i++) {
    if (cells[i]!.mine) continue;
    let count = 0;
    for (const n of neighbours(i)) if (cells[n]!.mine) count++;
    cells[i]!.adjacent = count;
  }
  return cells;
}

export function createGameState(rng: Rng = Math.random, firstTurn: Team = "red"): ServerGameState {
  return {
    cells: generateBoard(rng),
    scores: { red: 0, blue: 0 },
    turn: firstTurn,
    status: "active",
    winner: null,
    bombsUsed: { red: false, blue: false },
    minesClaimed: 0,
  };
}

export interface MoveResult {
  ok: boolean;
  error?: string;
  /** Indices revealed/claimed by this move (for incremental client updates). */
  revealed: number[];
  /** True if at least one mine was claimed (turn is retained). */
  mineHit: boolean;
  turnAfter: Team;
  gameOver: boolean;
  winner: Team | null;
}

function fail(state: ServerGameState, error: string): MoveResult {
  return {
    ok: false,
    error,
    revealed: [],
    mineHit: false,
    turnAfter: state.turn,
    gameOver: state.status === "finished",
    winner: state.winner,
  };
}

/** Reveal a single safe cell, flood-filling connected empties. Returns revealed indices. */
function floodReveal(state: ServerGameState, start: number, team: Team): number[] {
  const revealed: number[] = [];
  const stack = [start];
  while (stack.length) {
    const i = stack.pop()!;
    const cell = state.cells[i]!;
    if (cell.revealed || cell.mine) continue;
    cell.revealed = true;
    cell.owner = team;
    revealed.push(i);
    if (cell.adjacent === 0) {
      for (const n of neighbours(i)) {
        const nc = state.cells[n]!;
        if (!nc.revealed && !nc.mine) stack.push(n);
      }
    }
  }
  return revealed;
}

/**
 * Evaluate win conditions after a claim, updating status/winner in place.
 * Returns whether the game is now over (callers use this rather than re-reading
 * `state.status`, which TypeScript has narrowed via the earlier turn guard).
 */
function evaluateWin(state: ServerGameState): { over: boolean; winner: Team | null } {
  const { red, blue } = state.scores;
  if (red >= WIN_THRESHOLD || blue >= WIN_THRESHOLD || state.minesClaimed >= MINE_COUNT) {
    state.status = "finished";
    state.winner = red === blue ? null : red > blue ? "red" : "blue";
    return { over: true, winner: state.winner };
  }
  return { over: false, winner: null };
}

/**
 * Apply a normal reveal at (x,y) for `team`. Claiming a mine keeps the turn and
 * scores a point; revealing a safe cell passes the turn.
 */
export function applyMove(state: ServerGameState, team: Team, x: number, y: number): MoveResult {
  if (state.status !== "active") return fail(state, "game_not_active");
  if (state.turn !== team) return fail(state, "not_your_turn");
  if (!inBounds(x, y)) return fail(state, "out_of_bounds");
  const i = index(x, y);
  const cell = state.cells[i]!;
  if (cell.revealed) return fail(state, "already_revealed");

  if (cell.mine) {
    cell.revealed = true;
    cell.owner = team;
    state.scores[team] += 1;
    state.minesClaimed += 1;
    const outcome = evaluateWin(state);
    return {
      ok: true,
      revealed: [i],
      mineHit: true,
      turnAfter: state.turn, // keep turn on a mine
      gameOver: outcome.over,
      winner: outcome.winner,
    };
  }

  const revealed = floodReveal(state, i, team);
  state.turn = otherTeam(team);
  return {
    ok: true,
    revealed,
    mineHit: false,
    turnAfter: state.turn,
    gameOver: false,
    winner: null,
  };
}

/**
 * Detonate the one-per-player bomb centred at (x,y): reveal a 3x3 area, claiming
 * every mine in it. Turn is retained if any mine was claimed, otherwise passes.
 */
export function applyBomb(state: ServerGameState, team: Team, x: number, y: number): MoveResult {
  if (state.status !== "active") return fail(state, "game_not_active");
  if (state.turn !== team) return fail(state, "not_your_turn");
  if (state.bombsUsed[team]) return fail(state, "bomb_already_used");
  if (!inBounds(x, y)) return fail(state, "out_of_bounds");

  const revealed: number[] = [];
  let mineHit = false;
  for (let dy = -BOMB_RADIUS; dy <= BOMB_RADIUS; dy++) {
    for (let dx = -BOMB_RADIUS; dx <= BOMB_RADIUS; dx++) {
      const nx = x + dx;
      const ny = y + dy;
      if (!inBounds(nx, ny)) continue;
      const i = index(nx, ny);
      const cell = state.cells[i]!;
      if (cell.revealed) continue;
      if (cell.mine) {
        cell.revealed = true;
        cell.owner = team;
        state.scores[team] += 1;
        state.minesClaimed += 1;
        mineHit = true;
        revealed.push(i);
      } else {
        for (const r of floodReveal(state, i, team)) revealed.push(r);
      }
    }
  }

  state.bombsUsed[team] = true;
  const outcome = mineHit ? evaluateWin(state) : { over: false, winner: null };
  if (!mineHit) state.turn = otherTeam(team);
  return {
    ok: true,
    revealed,
    mineHit,
    turnAfter: state.turn,
    gameOver: outcome.over,
    winner: outcome.winner,
  };
}

/**
 * End an active game because `loser` forfeited (resigned) or dropped out — the
 * other team wins regardless of score. Returns the outcome, or a no-op if the
 * game isn't active.
 */
export function resign(state: ServerGameState, loser: Team): { over: boolean; winner: Team | null } {
  if (state.status !== "active") return { over: false, winner: null };
  state.status = "finished";
  state.winner = otherTeam(loser);
  return { over: true, winner: state.winner };
}

/** Project the authoritative board into the client-visible cell array. */
export function toClientCells(cells: ServerCell[]): ClientCell[] {
  return cells.map((c) => {
    if (!c.revealed) return { state: "hidden" };
    if (c.mine) return { state: "mine", owner: c.owner ?? "red" };
    return { state: "safe", adjacent: c.adjacent, owner: c.owner ?? "red" };
  });
}

export function minesRemaining(state: ServerGameState): number {
  return MINE_COUNT - state.minesClaimed;
}
